'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/lib/auth/auth-context';
import { SupportedCurrency, DEFAULT_CURRENCY } from './types';
import { normalizeCurrency } from './utils';

const CURRENCY_STORAGE_KEY = 'vendor_currency_preference';

// Helper functions for localStorage operations
const getStoredCurrency = (vendorId: string): SupportedCurrency | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const key = `${CURRENCY_STORAGE_KEY}_${vendorId}`;
    const stored = localStorage.getItem(key);
    const normalized = stored ? normalizeCurrency(stored) : null;
    return stored && normalized !== DEFAULT_CURRENCY ? normalized : null;
  } catch (error) {
    console.error('❌ getStoredCurrency error:', error);
    return null;
  }
};

const setStoredCurrency = (vendorId: string, currency: SupportedCurrency): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const key = `${CURRENCY_STORAGE_KEY}_${vendorId}`;
    localStorage.setItem(key, currency);
  } catch (error) {
    console.error('❌ setStoredCurrency error:', error);
  }
};

interface CurrencyContextType {
  currency: SupportedCurrency;
  setCurrency: (currency: SupportedCurrency) => void;
  isLoading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

interface CurrencyProviderProps {
  children: React.ReactNode;
}

export function CurrencyProvider({ children }: CurrencyProviderProps) {
  const { user, isAuthReady } = useAuth();
  // Initialize currency state with localStorage fallback if available
  const [currency, setCurrencyState] = useState<SupportedCurrency>(() => {
    // Only try localStorage if we have a vendorId and we're on the client
    if (typeof window !== 'undefined' && user?.vendorId) {
      const storedCurrency = getStoredCurrency(user.vendorId);
      return storedCurrency || DEFAULT_CURRENCY;
    }
    return DEFAULT_CURRENCY;
  });
  const [isLoading, setIsLoading] = useState(true);

  // Initialize currency from localStorage if available
  useEffect(() => {
    if (user?.vendorId) {
      const storedCurrency = getStoredCurrency(user.vendorId);
      if (storedCurrency) {
        setCurrencyState(storedCurrency);
      }
    }
  }, [user?.vendorId]);

  // Load vendor's preferred currency from the database
  useEffect(() => {
    let isMounted = true;

    async function loadVendorCurrency() {
      // Wait for auth to be ready before attempting to load currency
      if (!isAuthReady) {
        return;
      }

      if (!user?.vendorId) {
        if (isMounted) {
          setCurrencyState(DEFAULT_CURRENCY);
          setIsLoading(false);
        }
        return;
      }

      if (isMounted) {
        setIsLoading(true);
      }

      try {
        const response = await fetch(`/api/vendors/${user.vendorId}/currency`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
          },
        });
        
        if (response.ok) {
          const response_data = await response.json();
          const vendorCurrency = normalizeCurrency(response_data.data.preferred_currency);
          if (isMounted) {
            setCurrencyState(vendorCurrency);
            // Sync with localStorage
            setStoredCurrency(user.vendorId, vendorCurrency);
          }
        } else {
          // Try to use stored currency as fallback
          const storedCurrency = getStoredCurrency(user.vendorId);
          if (isMounted) {
            setCurrencyState(storedCurrency || DEFAULT_CURRENCY);
          }
        }
      } catch (error) {
        console.warn('❌ Failed to load vendor currency preference:', error);
        // Try to use stored currency as fallback
        const storedCurrency = getStoredCurrency(user.vendorId);
        if (isMounted) {
          setCurrencyState(storedCurrency || DEFAULT_CURRENCY);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadVendorCurrency();

    return () => {
      isMounted = false;
    };
  }, [user?.vendorId, isAuthReady]);

  // Update vendor's currency preference in the database
  const setCurrency = useCallback(async (newCurrency: SupportedCurrency) => {
    if (!user?.vendorId) {
      console.warn('❌ Cannot set currency: No vendor ID available');
      return;
    }

    // Don't update if it's the same currency
    if (newCurrency === currency) {
      return;
    }

    // Store the previous currency for potential revert
    const previousCurrency = currency;

    // Optimistically update the UI and localStorage
    setCurrencyState(newCurrency);
    setStoredCurrency(user.vendorId, newCurrency);

    try {
      const response = await fetch(`/api/vendors/${user.vendorId}/currency`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify({ preferred_currency: newCurrency }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update currency preference: ${response.status} - ${errorText}`);
      }

      // Verify the update was successful
      await response.json();
    } catch (error) {
      console.error('❌ Failed to update currency preference:', error);
      // Revert on error
      setCurrencyState(previousCurrency);
      setStoredCurrency(user.vendorId, previousCurrency);
      throw error; // Re-throw so the UI can handle it
    }
  }, [user?.vendorId, currency]);

  const value: CurrencyContextType = useMemo(() => ({
    currency,
    setCurrency,
    isLoading,
  }), [currency, setCurrency, isLoading]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency(): CurrencyContextType {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}